/**
 * Per-node handle-visibility computation (pure, unit-testable).
 *
 * Pairs with `renderActiveEdgesOnly` (Change A): when the canvas renders only
 * "active" edges, it should also render handles only for the nodes those edges
 * actually touch — otherwise every node keeps paying ReactFlow handle-bounds
 * measurement even though its handles are never used. This computes which node
 * IDs should mount their connection handles.
 *
 * CRITICAL — edge anchoring correctness: ReactFlow positions an edge from its
 * source/target *handle bounds*. If a visible edge has an endpoint whose handles
 * are unmounted, that endpoint loses its anchor and the edge snaps to a fallback
 * position. So the visible set MUST include BOTH endpoints of every visible edge,
 * not just the "active" nodes. `visibleEdges` only contains edges touching an
 * active node, so this union stays O(active nodes + their direct neighbors).
 */

export interface HandleVisibility {
  /** Master override (alwaysHideEdges): when true, no node renders handles. */
  hideAll: boolean
  /**
   * Node IDs whose handles should mount. `null` is the "all nodes visible"
   * sentinel (the `renderActiveEdgesOnly === false` escape hatch).
   */
  visibleNodeIds: Set<string> | null
}

interface EdgeEndpoints {
  source: string
  target: string
}

export interface ComputeHandleVisibilityOptions {
  alwaysHideEdges: boolean
  renderActiveEdgesOnly: boolean
}

const EMPTY_VISIBLE_NODE_IDS: Set<string> = new Set<string>()

/** All-visible result (handles render for every node). Stable reference. */
export const ALL_HANDLES_VISIBLE: HandleVisibility = {
  hideAll: false,
  visibleNodeIds: null,
}

/** Hide-all result (alwaysHideEdges master override). Stable reference. */
export const NO_HANDLES_VISIBLE: HandleVisibility = {
  hideAll: true,
  visibleNodeIds: EMPTY_VISIBLE_NODE_IDS,
}

export function computeHandleVisibility(
  activeVisualEdgeNodeIds: ReadonlySet<string>,
  visibleEdges: ReadonlyArray<EdgeEndpoints>,
  opts: ComputeHandleVisibilityOptions,
): HandleVisibility {
  // alwaysHideEdges is the master override — hide every handle.
  if (opts.alwaysHideEdges) return NO_HANDLES_VISIBLE
  // Escape hatch: when not gating edges, every node keeps its handles.
  if (!opts.renderActiveEdgesOnly) return ALL_HANDLES_VISIBLE

  const ids = new Set<string>(activeVisualEdgeNodeIds)
  // Endpoint union — required for edge anchoring (see file header).
  for (const edge of visibleEdges) {
    ids.add(edge.source)
    ids.add(edge.target)
  }
  return { hideAll: false, visibleNodeIds: ids }
}

/** Project the per-node boolean: are this node's handles visible? */
export function isNodeHandleVisible(
  visibility: HandleVisibility,
  nodeId: string,
): boolean {
  if (visibility.hideAll) return false
  if (visibility.visibleNodeIds === null) return true
  return visibility.visibleNodeIds.has(nodeId)
}

/** True when two visibility snapshots are observationally identical. */
export function handleVisibilityEqual(
  a: HandleVisibility,
  b: HandleVisibility,
): boolean {
  if (a === b) return true
  if (a.hideAll !== b.hideAll) return false
  const sa = a.visibleNodeIds
  const sb = b.visibleNodeIds
  if (sa === sb) return true
  if (sa === null || sb === null) return false
  if (sa.size !== sb.size) return false
  for (const id of sa) {
    if (!sb.has(id)) return false
  }
  return true
}
